import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight } from "lucide-react";

const categories = [
  { id: "all", label: "Tutti" },
  { id: "stampa", label: "Stampa & Grafica" },
  { id: "sartoria", label: "Artigianato" },
  { id: "scuola", label: "Scuola / PCTO" },
  { id: "erasmus", label: "Erasmus" },
  { id: "adulti", label: "Adulti" },
];

export default function Servizi() {
  const [labs, setLabs] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/labs").then((r) => setLabs(r.data)).catch(() => setLabs([]));
  }, []);

  const filtered = tab === "all" ? labs : labs.filter((l) => l.category === tab);

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="overline">Servizi & Laboratori</div>
        <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] mt-3 max-w-3xl">
          Dalla stampa alle arti applicate: laboratori per tutte le età.
        </h1>
        <p className="mt-6 max-w-2xl text-[#6B6960] text-lg leading-relaxed">
          Il Centro Artistico della Corte della Miniera propone laboratori differenti alle scuole
          e ai gruppi che trascorrono uno o più giorni in agriturismo. Ogni modulo dura 3 ore
          (mattino 9-12 · pomeriggio 15-18), minimo 15 partecipanti.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <Tabs value={tab} onValueChange={setTab} data-testid="servizi-tabs">
          <TabsList className="bg-transparent p-0 gap-2 flex flex-wrap justify-start h-auto">
            {categories.map((c) => (
              <TabsTrigger
                key={c.id}
                value={c.id}
                data-testid={`tab-${c.id}`}
                className="rounded-full border border-[#E2DFD6] px-4 py-2 data-[state=active]:bg-[#8C3A27] data-[state=active]:text-white"
              >
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={tab} className="mt-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((lab) => (
                <div key={lab.id} data-testid={`lab-card-${lab.slug}`} className="border border-[#E2DFD6] bg-white hover-lift overflow-hidden">
                  {lab.image_url && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={lab.image_url} alt={lab.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="overline">{lab.category}</div>
                    <h3 className="font-display text-2xl mt-2">{lab.title}</h3>
                    <p className="mt-2 text-sm text-[#6B6960] leading-relaxed">{lab.description}</p>
                    <div className="mt-4 text-sm text-[#1A1A1A]">
                      {lab.duration_label ? `${lab.duration_label} · ` : ""}minimo {lab.min_participants} partecipanti (max {lab.max_participants})
                    </div>
                    {lab.long_description && (
                      <button type="button" onClick={() => setOpen(lab)} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#8C3A27] hover:gap-3 transition-all" data-testid={`lab-more-${lab.slug}`}>
                        Scopri il laboratorio <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {filtered.length === 0 && <div className="text-[#6B6960]">Nessun laboratorio in questa categoria.</div>}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="lab-detail-dialog">
            {open && (
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="aspect-[4/3] md:aspect-auto md:min-h-full overflow-hidden">
                  <img src={open.image_url} alt={open.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-10">
                  <DialogHeader>
                    <div className="overline">{open.category} · {open.title}</div>
                    <DialogTitle className="font-display text-2xl md:text-3xl leading-tight mt-2 text-left" data-testid="lab-detail-title">{open.long_title || open.title}</DialogTitle>
                  </DialogHeader>
                  <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#1A1A1A]" data-testid="lab-detail-text">
                    {open.long_description.split("\n\n").map((p, i) => <p key={i} className={i === 0 ? "font-display text-xl text-[#8C3A27]" : ""}>{p}</p>)}
                  </div>
                  <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                    <span className="text-sm text-[#6B6960]">{open.duration_label ? `${open.duration_label} · ` : ""}minimo {open.min_participants} partecipanti (max {open.max_participants})</span>
                    <Link to="/prenota"><Button className="btn-primary-cdm rounded-full h-11 px-6">Richiedi preventivo</Button></Link>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[#E2DFD6] pt-10">
          <h3 className="font-display text-3xl">Pronto a costruire il tuo programma?</h3>
          <Link to="/prenota"><Button className="btn-primary-cdm rounded-full h-12 px-7">Prenota ora</Button></Link>
        </div>
      </section>
    </Layout>
  );
}
